import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { columnsData } from './columns';
import { Grid } from 'gridjs-react';
import ApiService from '@/services/api';
import GeneralLedgerTable from './GeneralLedgerTable';
import dayjs from 'dayjs';

const GeneralLedger = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
  });

  // Load orders on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem("user_profile"));
      const cooperativeId = profile?.cooperative.id || 0;

      const response = await ApiService.getJournalEntries(
        cooperativeId,
        filters.startDate,
        filters.endDate,
        '', // search
        page,
        limit
      );

      if (response.status === 200) {
        setReport(response.data);
        
        // Update pagination info from response
        if (response.pagination) {
          setPagination({
            page: response.pagination.current_page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.total_pages
          });
        }
      }
    } catch (error) {
      console.error(error);
      setReport([]);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleEdit = (journalId) => {
    // Navigate ke halaman edit dengan query parameter id
    navigate(`/admin/report/general-ledger/add?id=${journalId}`);
  };

  const handleDelete = async (journalId) => {
    // Konfirmasi sebelum menghapus
    const result = await Swal.fire({
      title: "Apakah Anda yakin?",
      text: "Data jurnal yang dihapus tidak dapat dikembalikan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
    });

    if (result.isConfirmed) {
      try {
        const response = await ApiService.deleteJournalEntry(journalId);

        if (response.status === 200 || response.success) {
          Swal.fire({
            icon: "success",
            title: "Berhasil!",
            text: "Jurnal berhasil dihapus",
            timer: 2000,
            showConfirmButton: false,
          });

          // Reload data setelah delete
          loadData();
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal!",
          text: error.message || "Gagal menghapus jurnal",
        });
      }
    }
  };

  return (
    <>
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <button
                onClick={() => navigate('/admin/report/general-ledger')}
                className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
              >
                Jurnal Umum
              </button>
            </div>
          </li>
        </ol>
      </nav>
    
      <div className="flex flex-auto flex-col">
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h4 className="card-title">Jurnal Umum</h4>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                />
                <button
                  className="btn bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={() => loadData(1)}
                  disabled={loading}
                >
                  <i className="mgc_search_line me-2"></i>Filter
                </button>
                <button
                  className="btn btn-sm bg-success text-white"
                  onClick={() => navigate('/admin/report/general-ledger/add')}
                >
                  <i className="mgc_add_line me-2"></i>Tambah Jurnal
                </button>
                <button 
                  className="btn btn-sm bg-secondary text-white"
                  onClick={() => loadData(1)}
                  disabled={loading}
                >
                  <i className="mgc_refresh_1_line me-2"></i>Refresh
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {/* Pagination Controls - Top */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Tampilkan:</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setPagination({ ...pagination, limit: newLimit, page: 1 });
                    loadData(1, newLimit);
                  }}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm w-16"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">data per halaman</span>
              </div>
              <div className="text-sm text-gray-600">
                Menampilkan {report.length > 0 ? ((pagination.page - 1) * pagination.limit + 1) : 0} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
              </div>
            </div>

            <div className="overflow-x-auto">
              <GeneralLedgerTable data={report} onEdit={handleEdit} onDelete={handleDelete} />
            </div>

            {/* Pagination Controls - Bottom */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-600">
                  Halaman {pagination.page} dari {pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadData(1)}
                    disabled={pagination.page === 1 || loading}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => loadData(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    « Prev
                  </button>
                  
                  {/* Page numbers */}
                  {[...Array(pagination.totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => loadData(pageNum)}
                          disabled={loading}
                          className={`px-3 py-1 border rounded text-sm disabled:cursor-not-allowed ${
                            pageNum === pagination.page
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === pagination.page - 2 ||
                      pageNum === pagination.page + 2
                    ) {
                      return <span key={pageNum} className="px-2">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => loadData(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages || loading}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Next »
                  </button>
                  <button
                    onClick={() => loadData(pagination.totalPages)}
                    disabled={pagination.page === pagination.totalPages || loading}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralLedger;
